/**
 * Microsoft Graph access, limited to this app's own OneDrive folder.
 *
 * `/me/drive/special/approot` is the folder Graph creates for the application;
 * the `Files.ReadWrite.AppFolder` scope cannot reach outside it. Four calls are
 * all this needs, so there is no Graph SDK — just fetch.
 */

import { getAccessToken } from './msal.ts';

const APPROOT = 'https://graph.microsoft.com/v1.0/me/drive/special/approot';

export class ConflictError extends Error {
  constructor(readonly documentName: string) {
    super(`Remote copy of ${documentName} changed since it was last read`);
    this.name = 'ConflictError';
  }
}

export class GraphError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(`Graph request failed (${status}): ${detail}`);
    this.name = 'GraphError';
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  return { Authorization: `Bearer ${await getAccessToken()}` };
}

export interface RemoteDocument {
  content: unknown;
  eTag: string | null;
  lastModified: string | null;
}

/** Reads a JSON document. Returns null when it does not exist yet. */
export async function readDocument(name: string): Promise<RemoteDocument | null> {
  const headers = await authHeaders();

  // Metadata first: the content endpoint 302s to a pre-signed storage URL that
  // does not carry the eTag we need for conditional writes.
  const metaResponse = await fetch(`${APPROOT}:/${name}`, { headers });
  if (metaResponse.status === 404) return null;
  if (!metaResponse.ok) {
    throw new GraphError(metaResponse.status, await metaResponse.text());
  }

  const meta = (await metaResponse.json()) as {
    eTag?: string;
    lastModifiedDateTime?: string;
  };

  const contentResponse = await fetch(`${APPROOT}:/${name}:/content`, { headers });
  if (contentResponse.status === 404) return null;
  if (!contentResponse.ok) {
    throw new GraphError(contentResponse.status, await contentResponse.text());
  }

  const text = await contentResponse.text();
  if (text.trim().length === 0) return null;

  return {
    content: JSON.parse(text) as unknown,
    eTag: meta.eTag ?? null,
    lastModified: meta.lastModifiedDateTime ?? null,
  };
}

export interface WriteResult {
  eTag: string | null;
  lastModified: string | null;
}

/**
 * Writes a JSON document.
 *
 * `expectedETag` makes the write conditional: Graph rejects it with 412 if the
 * remote copy changed since we read it, which is what turns a silent overwrite
 * into a conflict the user gets told about. Pass null only when creating a
 * document that genuinely does not exist yet.
 */
export async function writeDocument(
  name: string,
  content: unknown,
  expectedETag: string | null,
): Promise<WriteResult> {
  const headers: Record<string, string> = {
    ...(await authHeaders()),
    'Content-Type': 'application/json',
  };

  if (expectedETag !== null) {
    headers['if-match'] = expectedETag;
  }

  const response = await fetch(`${APPROOT}:/${name}:/content`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(content),
  });

  if (response.status === 412) throw new ConflictError(name);
  if (!response.ok) throw new GraphError(response.status, await response.text());

  const meta = (await response.json()) as { eTag?: string; lastModifiedDateTime?: string };
  return { eTag: meta.eTag ?? null, lastModified: meta.lastModifiedDateTime ?? null };
}

export interface GraphProfile {
  displayName: string;
  userPrincipalName: string;
}

export async function readProfile(): Promise<GraphProfile> {
  const response = await fetch(
    'https://graph.microsoft.com/v1.0/me?$select=displayName,userPrincipalName',
    { headers: await authHeaders() },
  );
  if (!response.ok) throw new GraphError(response.status, await response.text());

  const body = (await response.json()) as Partial<GraphProfile>;
  return {
    displayName: body.displayName ?? '',
    userPrincipalName: body.userPrincipalName ?? '',
  };
}
